import React, { FC } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { CellularBilledController, CellularPayPrompt } from './types'
import { color } from './tokens'
import { formatDate, formatMoney, formatMonthYear } from './format'

// The bill by controller (PM, 2026-09-30): the Lovable design assumed one expired
// controller, so its year rows did not say whose years they were. Each controller
// now shows when it expired, the years the two-year cap forgave it (struck), and the
// years it is billed.

const useStyles = makeStyles({
  // Owners with many controllers scroll inside the bill, not the whole dialog.
  list: { maxHeight: 240, overflowY: 'auto' },
  controller: {
    padding: '6px 0',
    borderTop: `1px solid ${color.border}`,
    '&:first-child': { borderTop: 'none', paddingTop: 0 }
  },
  name: { fontSize: 13, lineHeight: '18px', fontWeight: 600, color: color.foreground },
  expired: { fontWeight: 400, color: color.mutedForeground },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '2px 0 0 12px',
    fontSize: 13,
    lineHeight: '18px'
  },
  span: { color: color.mutedForeground },
  amount: { fontWeight: 500, color: color.foreground, whiteSpace: 'nowrap' },
  forgiven: { color: color.mutedForeground, textDecoration: 'line-through', whiteSpace: 'nowrap' }
})

const yearsText = (years: number): string => `${years} ${years === 1 ? 'year' : 'years'}`

// "2 years", "353 days" or "2 years + 173 days": a controller added to a live
// subscription also pays the days after its whole years, up to the renewal (D57).
const billedText = (years: number, days?: number | null): string => {
  if (!days) return yearsText(years)
  const dayText = `${days} ${days === 1 ? 'day' : 'days'}`
  return years ? `${yearsText(years)} + ${dayText}` : dayText
}

const range = (from?: string | null, to?: string | null): string =>
  from && to ? `${formatMonthYear(from)} – ${formatMonthYear(to)} · ` : ''

/** Every controller has its billed span: the bill can be shown by controller. */
export const billsByController = (prompt: CellularPayPrompt): boolean => {
  const controllers = prompt.billedControllers || []
  return prompt.isBackbill && controllers.length > 0 && controllers.every(c => Boolean(c.billedFrom && c.billedTo))
}

const ControllerRows: FC<{ c: CellularBilledController; currency: string }> = ({ c, currency }) => {
  const classes = useStyles()
  return (
    <div
      className={classes.controller}
      data-testid={`cellular-bill-controller-${c.serial}`}
    >
      <div className={classes.name}>
        {c.model ? `${c.serial} (${c.model})` : c.serial}
        <span className={classes.expired}>
          {c.expiredOn ? ` · expired ${formatDate(c.expiredOn)}` : ''}
        </span>
      </div>
      {(c.forgivenYears || 0) > 0 && (
        <div className={classes.row}>
          <span className={classes.span}>
            {`${range(c.forgivenFrom, c.forgivenTo)}${yearsText(c.forgivenYears || 0)} not billed`}
          </span>
          <span className={classes.forgiven}>{formatMoney(c.forgivenUsd || 0, currency)}</span>
        </div>
      )}
      <div className={classes.row}>
        <span className={classes.span}>{`${range(c.billedFrom, c.billedTo)}${billedText(c.years, c.partYearDays)}`}</span>
        <span className={classes.amount}>{formatMoney(c.amountUsd, currency)}</span>
      </div>
    </div>
  )
}

export const ControllerBill: FC<{ prompt: CellularPayPrompt }> = ({ prompt }) => {
  const classes = useStyles()
  return (
    <div
      className={classes.list}
      data-testid='cellular-bill-by-controller'
    >
      {(prompt.billedControllers || []).map(c => (
        <ControllerRows
          c={c}
          currency={prompt.currency}
          key={`${c.serial}-${c.expiredOn}`}
        />
      ))}
    </div>
  )
}

// "Next renewal: May 20, 2027 — $720.00 a year for 3 controllers."
export const renewalText = (prompt: CellularPayPrompt): string | null => {
  const seats = prompt.renewalSeats
  if (!prompt.nextInvoiceDate || prompt.renewalAmountUsd == null || !seats) return null
  return (
    `Next renewal: ${formatDate(prompt.nextInvoiceDate)} — ` +
    `${formatMoney(prompt.renewalAmountUsd, prompt.currency)} a year for ${seats} ` +
    `${seats === 1 ? 'controller' : 'controllers'}.`
  )
}
